import { useCallback, useEffect } from "react";
import { FieldPath, UseFormReturn } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  isLoadingUsersAd,
  translate,
  usersAdOptionsSelector,
  usersThunks,
} from "@shape/reef-shared";
import type { AppDispatch, AssessmentTeamResponse } from "@shape/reef-shared";
import { HiddenField, MultipleSelect } from "components/Form";
import type { CreateStep2MdbFormValues } from "types";
import { TeamMember } from "pages/cto/pages/NewCtoDetails/components/NewStep1Initiation/CreateAssessmentTeam/TeamMember";
import { SectionTitle } from "pages/cto/pages/NewCtoDetails/components/SectionTitle";
import { Box } from "ui/styled";
import styles from "../../NewStep2Evaluation.module.scss";
import {
  getStep2AssessmentTeamAdditionalUsers,
  getStep2AssessmentTeamDefaultValue,
  step2AdditionalPosition,
  step2FixedPositions,
} from "./step2AssessmentTeam.helpers";

interface Step2AssessmentTeamProps {
  readonly form: UseFormReturn<CreateStep2MdbFormValues>;
  readonly readOnly: boolean;
  readonly initialValues?: AssessmentTeamResponse[];
}

export function Step2AssessmentTeam({ form, initialValues, readOnly }: Step2AssessmentTeamProps) {
  const dispatch = useDispatch<AppDispatch>();
  const usersOptions = useSelector(usersAdOptionsSelector());
  const isLoadingUsers = useSelector(isLoadingUsersAd);
  const additionalUsers = getStep2AssessmentTeamAdditionalUsers(initialValues);
  const prefix = "assessment_team";

  useEffect(() => {
    !usersOptions.length && dispatch(usersThunks.fetchUsersByQuery("", 1000));
  }, [dispatch, usersOptions.length]);

  const updateUsersByQuery = useCallback(
    (query: string) => {
      dispatch(usersThunks.fetchUsersByQuery(query));
    },
    [dispatch]
  );

  return (
    <>
      <SectionTitle>{translate("ctoStep1.assessmentTeamMembers")}</SectionTitle>
      <div className={styles.assessmentTeamGrid}>
        {step2FixedPositions.map((position, index) => (
          <div className={styles.teamMemberWrapper} key={`${prefix}[${index}]`}>
            <TeamMember<CreateStep2MdbFormValues>
              name={`${prefix}[${index}].user_id` as FieldPath<CreateStep2MdbFormValues>}
              title={translate(position.title)}
              form={form}
              onFilterChange={updateUsersByQuery}
              readOnly
              options={usersOptions}
              maxResults={100}
              defaultValue={getStep2AssessmentTeamDefaultValue(initialValues, position.id)}
              isLoadingUsers={isLoadingUsers}
            />
            <HiddenField
              name={`${prefix}[${index}].role_code` as FieldPath<CreateStep2MdbFormValues>}
              valueState={position.id}
              defaultValue={position.id}
              form={form}
            />
          </div>
        ))}
      </div>
      <Box>
        <MultipleSelect
          form={form}
          readOnly={readOnly}
          options={usersOptions}
          name="assessment_team_plus"
          label={translate(step2AdditionalPosition.title)}
          subtitle={translate(step2AdditionalPosition.subtitle)}
          placeholder={translate(step2AdditionalPosition.label)}
          defaultValue={additionalUsers?.map((user) => user.user_id) ?? null}
          onFilterChange={updateUsersByQuery}
        />
      </Box>
    </>
  );
}
